import React from 'react';
import {Document, Page, Text, View, StyleSheet, Image} from '@react-pdf/renderer';
import {BingoCard, BingoTheme} from '../types';
const logoColorido = '/assets/images/logo-colorido.svg';
const logoPB = '/assets/images/logo-pb.svg';

const createStyles = (theme: BingoTheme) => {
    const isHighContrast = theme.id === 'high-contrast' || theme.id === 'default';
    return StyleSheet.create({
        page: {padding: 20, flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between'},
        cardContainer: {
            width: '49%',
            marginBottom: 15,
            border: `1pt solid ${theme.primary}`,
            padding: 5,
            backgroundColor: theme.secondary,
            position: 'relative',
            overflow: 'hidden'
        },
        watermark: {
            width: '60%',
            opacity: 0.15,
        },
        logo: {
            height: 30,
            width: 'auto',
        },
        title: {
            fontSize: 12,
            textAlign: 'center',
            marginBottom: 2,
            color: isHighContrast ? theme.primary : theme.accent,
            fontWeight: 'bold'
        },
        subtitle: {
            fontSize: 7,
            textAlign: 'right',
            marginBottom: 5,
            color: isHighContrast ? theme.primary : theme.accent,
        },
        grid: {display: 'flex', flexDirection: 'column'},
        row: {
            display: 'flex',
            flexDirection: 'row',
        },
        rowFirst: {},
        rowEvery: {
            borderBottom: `1pt solid ${theme.primary}`,
        },
        rowHeader: {
            borderBottom: isHighContrast ? `1pt solid ${theme.primary}` : '0pt',
        },
        cell: {
            width: '20%', minHeight: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.secondary,
        },
        cellFirst: {
            borderLeft: `1pt solid ${theme.primary}`,
        },
        cellEvery: {
            borderRight: `1pt solid ${theme.primary}`,
        },
        cellText: {
            fontSize: 8,
            color: theme.primary,
            textAlign: 'center',
            hyphens: 'none'
        },
        headerCell: {
            width: '20%', minHeight: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 5,
            paddingBottom: 5,
            backgroundColor: isHighContrast ? theme.secondary : theme.primary,
            border: '0pt'
        },
        headerCellText: {
            color: isHighContrast ? theme.primary : theme.secondary,
            fontWeight: 'bold',
        },
        namesPage: {padding: 30},
        namesTitle: {fontSize: 18, marginBottom: 15, fontWeight: 'bold'},
        namesContainer: {flexDirection: 'row', flexWrap: 'wrap'},
        nameItem: {width: '25%', fontSize: 10, marginBottom: 5}
    });
};

const chunkArray = (array: any[], size: number) => {
    console.log("[DEBUG_LOG] chunkArray - Chunking array...");
    const result = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
};

const BingoPDF = ({cards, imagesMap, logoDataMap, type, names, theme}: {
    cards: BingoCard[],
    imagesMap: Record<string, string>,
    logoDataMap: Record<string, string>,
    type: string,
    names: string[],
    theme: BingoTheme
}) => {
    console.log("[DEBUG_LOG] BingoPDF - Rendering PDF with cards:", cards.length, "Type:", type, "Theme:", theme.name);
    const cardChunks = chunkArray(cards, 8);
    const styles = createStyles(theme);

    const getLogoSrc = () => {
        const logoPath = theme.id === 'default' ? logoPB : logoColorido;
        const logoData = logoDataMap[logoPath];
        console.log(`[DEBUG_LOG] BingoPDF.getLogoSrc - Path: ${logoPath}, Data found: ${!!logoData}`);
        return logoData || logoPath;
    };

    const logoSrc = getLogoSrc();

    return (
        <Document>
            {cardChunks.map((chunk: BingoCard[], pIdx: number) => (
                <Page key={pIdx} size="A4" style={styles.page}>
                    {chunk.map((card: BingoCard, idx: number) => (
                        <View key={idx} style={styles.cardContainer}>
                            <Text style={styles.title}>{card.title}</Text>
                            <Text style={styles.subtitle}>Crie sua cartela em gritebingo...</Text>
                            <View style={styles.grid}>
                                <View style={[styles.row, styles.rowHeader]}>
                                    {card.header.map((cell: string, cIdx: number) => (
                                        <View key={cIdx} style={[
                                            styles.headerCell
                                        ]}>
                                            <View style={{ width: '100%', padding: 2 }}>
                                                <Text style={[styles.cellText, styles.headerCellText]}>{cell}</Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                                {card.rows.map((row: string[], rIdx: number) => (
                                    <View key={rIdx}
                                          style={[styles.row, rIdx === 0 ? styles.rowFirst : {}, styles.rowEvery]}>
                                        {row.map((cell: string, cIdx: number) => (
                                            <View key={cIdx} style={[
                                                styles.cell,
                                                cIdx === 0 ? styles.cellFirst : {},
                                                styles.cellEvery
                                            ]}>
                                                {rIdx === 2 && cIdx === 2 && card.imageId && imagesMap[card.imageId] ? (
                                                    <Image src={imagesMap[card.imageId]}
                                                           style={{width: 15, height: 15}}/>
                                                ) : (
                                                    <View style={{ width: '100%', padding: 2 }}>
                                                        <Text style={styles.cellText}>{cell}</Text>
                                                    </View>
                                                )}
                                            </View>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        </View>
                    ))}
                </Page>
            ))}
            {type === 'names' && names.length > 0 && (
                <Page size="A4" style={styles.namesPage}>
                    <Text style={styles.namesTitle}>Nomes Utilizados</Text>
                    <View style={styles.namesContainer}>
                        {names.map((name, idx) => (
                            <Text key={idx} style={styles.nameItem}>
                                {name}
                            </Text>
                        ))}
                    </View>
                </Page>
            )}
        </Document>
    );
};

export default BingoPDF;
